import {
  FluidDataColumn,
  FluidExportOptions,
  FluidMutationUpdate,
  FluidRowData,
  FluidTableAction,
  FluidTableHeader,
  getAtPath,
  setAtPath,
} from '@lmig/fluid-core';
import { exportToCsv } from '@lmig/fluid-core/lib/utils';

import { Utils } from '../../../utils/utils';

/**
 * Creates a default export action for the table actions along with a defaultHandlerKey to signify this action
 * should be handled by FLUID, and not by emitting the actionKey.
 * @param actionConfig
 */
export function createExportAction(
  actionConfig: FluidTableAction
): FluidTableAction & { defaultHandlerKey?: string } {
  const { label } = actionConfig.buttonConfig || { label: 'Export' };
  return {
    ...actionConfig,
    defaultHandlerKey: 'exportVisibleData',
    actionKey: actionConfig.actionKey || 'exportVisibleData',
    buttonConfig: {
      ...actionConfig.buttonConfig,
      label,
    },
  };
}

/**
 * Creates the default handler for the export action use, rather than emitting upwards for the consumer to use.
 * @param tableState
 * @param headers
 * @param exportOptions
 * @param tableId
 */
export function createExportHandler(
  tableState: FluidMutationUpdate,
  headers: FluidTableHeader[],
  exportOptions: FluidExportOptions,
  tableId: string
): void {
  const { exportableRows } = tableState;
  const exportableData = applyDataFormatting(
    headers,
    exportableRows,
    exportOptions
  );

  const columns = getExportColumns(headers, exportOptions.exportAll);
  exportOptions.exportFn
    ? exportOptions.exportFn(columns, exportableData)
    : exportToCsv(
        columns,
        exportableData,
        exportOptions.fileName || `${tableId}_export`
      );
}

/**
 * Filters the headers to those that are visible, and maps to a FluidDataColumn
 * array for export.
 * @param headers
 * @param exportAll
 */
function getExportColumns(
  headers: FluidTableHeader[],
  exportAll: boolean
): FluidDataColumn[] {
  return headers
    .filter((header: FluidTableHeader) => (exportAll ? true : header.visible))
    .map((header: FluidTableHeader) => ({
      dataPath: header.dataPath,
      label: header.label,
    }));
}

/**
 * Applies any data formatting to the exported data if configured to do so.
 * @param headers
 * @param dataItems
 * @param exportOptions
 */
function applyDataFormatting(
  headers: FluidTableHeader[],
  dataItems: FluidRowData[],
  exportOptions: FluidExportOptions
) {
  const shouldApplyFormatting =
    exportOptions.applyFormatting ||
    (!!exportOptions?.exportFormatter &&
      !Utils.isEmptyObject(exportOptions.exportFormatter));
  return shouldApplyFormatting
    ? dataItems.map((dataItem) => {
        headers.forEach((header: FluidTableHeader) => {
          dataItem = applyExportFormat(dataItem, header, exportOptions);
        });
        return dataItem;
      })
    : dataItems;
}

/**
 * If a formatter exists in export options, use it, otherwise, use dataFormat, otherwise, don't.
 * @param dataItem
 * @param header
 * @param exportOptions
 */
function applyExportFormat(
  dataItem: FluidRowData,
  header: FluidTableHeader,
  exportOptions: FluidExportOptions
) {
  if (exportOptions.exportFormatter?.[header.dataPath]) {
    dataItem = setAtPath(
      dataItem,
      exportOptions.exportFormatter?.[header.dataPath](dataItem),
      header.dataPath
    );
  } else if (header.dataFormat) {
    dataItem = setAtPath(
      dataItem,
      header.dataFormat(getAtPath(dataItem, header.dataPath)),
      header.dataPath
    );
  }
  return dataItem;
}
